import GeneralLayout from 'apps/main/layouts/GeneralLayout'
import CareerNavigation from 'apps/main/libs/path/feature-career-navigation'
import Faq from 'apps/main/libs/path/feature-faq'
import MembershipBenefit from 'apps/main/libs/path/feature-membership-benefit'
import { useRouter } from 'next/router'
import { PathType } from '@frontend-mono/shared/data-access-graphql'
import PathLayout from 'apps/main/layouts/path'
import CareerDescription from 'apps/main/libs/path/feature-career-description'
import { useMembershipRequired } from 'apps/main/libs/shared/feature-current-user'
import { useEnterPathEvent } from 'apps/main/libs/path/data-access-amplitude'
import {
  GET_PATH_INTRODUCE,
  useGetIntroduce,
  useGetPathSteps,
  useGetTargets,
} from '@frontend-mono/main/path/data-access-path'
import { useGetBestReviews } from 'apps/main/libs/path/data-access-reviews'
import Reviews from 'apps/main/libs/path/feature-reviews'
import TeamsIntroduce from 'apps/main/libs/teams/ui-teams-introduce'
import { CareerPathsResponse, GET_CAREER_PATHS } from 'apps/main/libs/navigation/data-access-path'
import { NX_PUBLIC_CLIENT_URL } from 'apps/main/env'
import CommonHead from 'apps/main/libs/shared/ui-common-head'
import Curriculum from 'apps/main/libs/path/feature-curriculum'
import { mapPathToTopicIds } from 'apps/main/libs/path/feature-curriculum/util'
import useSelectedTopics from 'apps/main/libs/path/feature-curriculum/useSelectedTopics'
import Introduce from 'apps/main/libs/path/feature-curriculum/Introduce'
import CareerGoals from 'apps/main/libs/path/feature-career-goals'
import { useTranslation } from 'next-i18next'
import { getStaticApolloProps } from 'apps/main/libs/shared/ssr/util-getStaticApolloProps'
import { getStaticApolloPaths } from 'apps/main/libs/shared/ssr/util-getStaticApolloPaths'
import { ProjectIntroduction } from 'apps/main/libs/path/feature-project-introduction'
import UiTargets from 'apps/main/libs/path/ui-targets'
import { isRenderingClient } from 'apps/main/deprecated/shared/functions'
import { useEffect } from 'react'
import { useMyRole } from '@frontend-mono/main/user/data-access-role'

export const getStaticPaths = getStaticApolloPaths(async ({ client }) => {
  const response = await client.query<CareerPathsResponse>({
    query: GET_CAREER_PATHS,
  })
  const paths = response.data?.careerPaths.map((careerPath) => ({
    params: { path_slug: careerPath.slug },
  }))

  return { paths, fallback: 'blocking' }
})

export const getStaticProps = getStaticApolloProps(async ({ params, client }) => {
  await client.query({
    query: GET_PATH_INTRODUCE,
    variables: {
      slug: params?.path_slug,
    },
  })

  return {
    props: {},
    revalidate: 60,
  }
})

const CareerPathPage = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { path_slug: slug } = router.query
  const { hasMembership } = useMembershipRequired()
  const { pathIntroduce } = useGetIntroduce(slug as string)
  useEnterPathEvent({ pathIntroduce, type: 'career' })
  const { isEmpty } = useGetBestReviews(slug as string)
  const { pathData, refetch } = useGetPathSteps(slug as string)
  const allTopicIds = mapPathToTopicIds(pathData)
  const { selectedTopicIds, handleCheckClick, selectAll, reset } = useSelectedTopics(allTopicIds)
  const { targets } = useGetTargets(slug as string)
  const { isAdmin } = useMyRole()

  useEffect(() => {
    if (isAdmin) {
      refetch({
        variables: {
          slug,
        },
      })
    }
  }, [
    isAdmin,
    slug,
    refetch,
  ])

  return (
    <GeneralLayout>
      <CommonHead
        title={`${pathIntroduce?.title}${t('CareerPath_HEAD_TITLE')}`}
        description={pathIntroduce?.description ?? ''}
        url={`${NX_PUBLIC_CLIENT_URL}/paths/career/${slug}`}
      />
      {isRenderingClient && (
        <PathLayout
          Navigation={<CareerNavigation currentPathSlug={slug as string} />}
          Introduce={
            <Introduce
              pathType={PathType.Career}
              pathSlug={slug as string}
              allTopicIds={allTopicIds}
              selectedTopicIds={selectedTopicIds}
              isSelectedTopicIdsEmpty={selectedTopicIds.length === 0}
              afterUpdateCallback={reset}
            />
          }
          Curriculum={
            <Curriculum
              pathData={pathData}
              pathType={PathType.Career}
              pathSlug={slug as string}
              allTopicIds={allTopicIds}
              selectedTopicIds={selectedTopicIds}
              selectAllTopics={selectAll}
              resetSelectedTopicIds={reset}
              onCheckClick={handleCheckClick}
            />
          }
          CareerGoals={<CareerGoals slug={slug as string} />}
          Description={<CareerDescription slug={slug as string} />}
          projectIntroduction={<ProjectIntroduction slug={slug as string} />}
          targets={<UiTargets targets={targets} />}
          Reviews={<Reviews pathSlug={slug as string} />}
          MembershipBenefit={
            hasMembership ? null : (
              <MembershipBenefit roundSide="left" pathIntroduceData={pathIntroduce} />
            )
          }
          Faq={<Faq />}
          TeamsIntroduce={<TeamsIntroduce />}
          shouldRenderReviews={!isEmpty}
        />
      )}
    </GeneralLayout>
  )
}

export default CareerPathPage
